import { AppDispatch } from '../redux/store';
import { getProjectProductsAction } from '../redux/actions/productActions';
import {
    getExternalProductsAction,
    getCompaniesOfProducts
} from '../redux/actions/transparencyCatalogActions';
import { ActionResponse } from '../models/actionResponse/types';
import { ExternalProduct, InternalProduct, Product } from '../models/product/types';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../redux/actions/transparencyCatalogActions/types';
import { mapLowEmittingData } from './lowEmittingHelper';
import { fetchTagsInBatches } from './projectProductTagBatchHelper';
import {
    collectDisclosuresFromProducts,
    fetchDisclosureImpactAreaMap,
    getProductImpactAreas,
    impactAreasToCSVString,
} from './cmfImpactAreaMapper';

export interface ExportProductsOptions {
    dispatch: AppDispatch;
    projectId: number;
    filterProducts: (products: Product[]) => Product[];
    generateFileName: (projectId: number) => string;
    onSuccess: (data: Product[], fileName: string) => void;
    onError: (message: string) => void;
}

/**
 * Shared logic for exporting project products to CSV with CMF impact areas
 */
export const exportProjectProducts = async ({
    dispatch,
    projectId,
    filterProducts,
    generateFileName,
    onSuccess,
    onError,
}: ExportProductsOptions): Promise<void> => {
    try {
        // Fetch internal products
        const { payload: internalProductsPayload } = (await dispatch(
            getProjectProductsAction(projectId),
        )) as ActionResponse<Array<InternalProduct>>;

        if (!internalProductsPayload?.data?.length) {
            onError('There are no products to export');
            return;
        }

        // Fetch external products
        const productIds = internalProductsPayload.data.map((p) => p.productId);
        const { payload: externalPayload } = (await dispatch(
            getExternalProductsAction({ productIds }),
        )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;

        if (!externalPayload?.data) {
            onError('Could not fetch product details');
            return;
        }

        // Flatten external products
        let availableProducts = Object.values(externalPayload.data.mfSection).reduce(
            (agg, item: any) => agg.concat(item),
            [] as ExternalProduct[],
        );

        // Fetch company data
        const companies = await getCompaniesOfProducts(availableProducts, dispatch);
        availableProducts = availableProducts.map((ep) => ({
            ...ep,
            companyLevel: companies.find((c) => c.id === ep.companyID)?.level,
            listingUrl: companies.find((c) => c.id === ep.companyID)?.listingUrl,
            websiteUrl: companies.find((c) => c.id === ep.companyID)?.websiteUrl,
        }));

        // Merge internal and external product data
        let currentProducts = internalProductsPayload.data.reduce((agg, value) => {
            const availableProduct = availableProducts.find(
                (product) => product.id === value.productId,
            );
            if (availableProduct) {
                agg.push({
                    ...availableProduct,
                    ...value,
                    dateAdded: value.dateAdded,
                    lastUpdated: value.lastUpdated,
                    product: value,
                    projectProductId: value.id,
                } as Product);
            }
            return agg;
        }, [] as Product[]);

        // Map low-emitting data
        await mapLowEmittingData(currentProducts, dispatch);

        const projectProductIds = internalProductsPayload.data
            .map((p) => p.id)
            .filter((id): id is number => id != null);
        const allTags = await fetchTagsInBatches(dispatch, projectProductIds);
        const tagsByProjectProductId = allTags.reduce((acc: Record<number, string[]>, tag) => {
            if (!acc[tag.projectProductId]) {
                acc[tag.projectProductId] = [];
            }
            const tagName = String(tag.tag?.name ?? tag.name ?? '').trim();
            if (tagName.length > 0) {
                acc[tag.projectProductId].push(tagName);
            }
            return acc;
        }, {});

        // Collect disclosures and fetch impact areas
        const allDisclosures = collectDisclosuresFromProducts(currentProducts);
        const disclosureImpactAreaMap = await fetchDisclosureImpactAreaMap(
            dispatch,
            allDisclosures,
        );

        // Apply custom filter (e.g., AIA categories, installed products, etc.)
        currentProducts = filterProducts(currentProducts);

        if (currentProducts.length === 0) {
            onError('No products found matching the filter criteria');
            return;
        }

        // Prepare export data with corrected text
        for (const element of currentProducts) {
            element.productNameCorrected = element.productName.replaceAll('"', '""');
            element.descriptionCorrected = element.description?.replaceAll('"', '""');
            if (element?.regulations?.includes('Perkins&Will Precautionary List Free' as any)) {
                element.hpdScopeResultsCorrected = 'Perkins&Will PL Free';
            } else {
                element.hpdScopeResultsCorrected = element.hpdScopeResults;
            }
        }

        // Map CMF impact areas for all disclosure types
        currentProducts = currentProducts.map((p) => {
            const impactAreas = getProductImpactAreas(p, disclosureImpactAreaMap);

            return {
                ...p,
                masterFormat: `${p.mfSection.number} ${p.mfSection.label}`,
                tagsCSV: (tagsByProjectProductId[p.projectProductId] ?? []).join(', '),
                // CMF Impact Areas - one comma-separated column per disclosure
                epdCmfImpactAreas: impactAreasToCSVString(impactAreas.epd),
                hpdCmfImpactAreas: impactAreasToCSVString(impactAreas.hpd),
                declareCmfImpactAreas: impactAreasToCSVString(impactAreas.declare),
                c2cCmfImpactAreas: impactAreasToCSVString(impactAreas.c2c),
                c2cMHCCmfImpactAreas: impactAreasToCSVString(impactAreas.c2cMHC),
                lpcCmfImpactAreas: impactAreasToCSVString(impactAreas.lpc),
                cleanAirSilverCmfImpactAreas: impactAreasToCSVString(impactAreas.cleanAirSilver),
                cleanAirGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.cleanAirGold),
                clearchemCmfImpactAreas: impactAreasToCSVString(impactAreas.clearChem),
                floorscoreCmfImpactAreas: impactAreasToCSVString(impactAreas.floorscore),
                greenCertifiedCmfImpactAreas: impactAreasToCSVString(impactAreas.greenCertified),
                greenguardCmfImpactAreas: impactAreasToCSVString(impactAreas.greenguard),
                greenguardGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.greenguardGold),
                indoorAdvantageCmfImpactAreas: impactAreasToCSVString(impactAreas.indoorAdvantage),
                indoorAdvantageGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.indoorAdvantageGold),
            };
        });

        // Sort by masterFormat, then by company
        currentProducts = currentProducts.sort((a, b) => {
            if (a.masterFormat === b.masterFormat) {
                return a.company < b.company ? -1 : 1;
            }
            return a.masterFormat < b.masterFormat ? -1 : 1;
        });

        const fileName = generateFileName(projectId);
        onSuccess(currentProducts, fileName);
    } catch (error) {
        console.error('Error exporting products:', error);
        onError('Failed to export products');
    }
};
